import type { TFunction } from "i18next";
import { EDIT_BLOCKED_REASON } from "../types/types.PIM";
import type { EditBlockedReason } from "../types/types.PIM";

const editBlockedReason = {
  [EDIT_BLOCKED_REASON.ATTRIBUTE_IS_IDENTIFIER]: (t: TFunction) =>
    t("This is a system attribute and cannot be edited."),
  [EDIT_BLOCKED_REASON.SCHEMA_LOCKED]: (t: TFunction) =>
    t("Attribute is not editable on the template."),
  [EDIT_BLOCKED_REASON.MASTER_OWNED_ATTRIBUTE]: (t: TFunction) =>
    t("Variant products cannot edit master-owned attributes."),
  [EDIT_BLOCKED_REASON.APPROVAL_IS_ACTIVE]: (t: TFunction) =>
    t("Product is pending approval."),
  [EDIT_BLOCKED_REASON.TEAM_DENIED]: (t: TFunction) =>
    t("User does not have edit permission for this product."),
  [EDIT_BLOCKED_REASON.NOT_IN_VIEW]: (t: TFunction) =>
    t("Attribute is not visible in your template view."),
  [EDIT_BLOCKED_REASON.ROLE_NOT_GRANTED]: (t: TFunction) =>
    t("Your role doesn't have edit permission for this attribute."),
} as const;

export function getEditBlockedReasonMessage(
  reason: EditBlockedReason | null | undefined,
  t: TFunction
): string | undefined {
  if (!reason) return undefined;
  return editBlockedReason[reason](t);
}

type AttributeEditRejected = {
  attribute_id: string;
  attribute_name: string;
  reason: string;
  message: string;
};

export type AttributeEditRejectedError = {
  error: "attribute_edit_rejected";
  message: string;
  rejected: AttributeEditRejected[];
};

export function isAttributeEditRejected(
  data: unknown
): data is AttributeEditRejectedError {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as AttributeEditRejectedError).error === "attribute_edit_rejected"
  );
}

export function getAttributeEditRejectedMessage(
  data: AttributeEditRejectedError,
  t: TFunction
): string {
  const names = data.rejected.map((r) => r.attribute_name).filter(Boolean);
  return names.length > 0
    ? t("Edit blocked for: {{names}}", { names: names.join(", ") })
    : t("Some attributes cannot be edited with your current role.");
}

/**
 * A system-default attribute is only locked from deletion/renaming while it
 * still lives inside a system-default group/collection; the same attribute
 * added to a non-system container remains editable.
 */
export function isSystemProtectedAttribute(
  containerIsSystem: boolean | undefined,
  attributeIsSystem: boolean | undefined
): boolean {
  return Boolean(containerIsSystem && attributeIsSystem);
}
